import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'
import { User } from '../../types/User'
import {
  useGetTribeByIdQuery,
  useUpdateTribeMutation,
} from '../../redux/tribe.endpoints'
import {
  useCreateTeamMutation,
  useUpdateTeamMutation,
} from '../../redux/team.endpoints'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router'
import Loader from '../../components/Loader/Loader'
import moment from 'moment-timezone'
import { Team } from '../../types/Team'
import { TeamModal, TeamFormData } from '../../components/TeamModal/TeamModal'

interface Props {
  user: User
}

const EditTribe = ({ user }: Props) => {
  const navigate = useNavigate()
  const { tribeId } = useParams()
  const parsedTribeId = parseInt(tribeId || '0')
  const {
    data: tribe,
    isLoading,
    isFetching,
    refetch: refreshTribe,
  } = useGetTribeByIdQuery(
    {
      companyId: user.company.id,
      tribeId: parsedTribeId,
    },
    {
      skip: !parsedTribeId,
      refetchOnMountOrArgChange: true,
    },
  )
  const [updatedTribeName, setUpdatedTribeName] = useState('')
  const [updateTribe, updateTribeResult] = useUpdateTribeMutation()
  const [createTeam] = useCreateTeamMutation()
  const [updateTeam] = useUpdateTeamMutation()
  const [sortedTeams, setSortedTeams] = useState([] as Team[])
  const [teamModal, setTeamModal] = useState<null | {
    mode: 'add' | 'edit'
    team?: Team
  }>(null)

  useEffect(() => {
    if (tribe) {
      setUpdatedTribeName(tribe.name)
    }
  }, [tribe])

  useEffect(() => {
    if (updateTribeResult && updateTribeResult.isError) {
      const error: any = updateTribeResult.error
      toast.error(
        `An error occurred when creating the tribe: ${error.data.error}`,
        {
          toastId: 'create-tribe-error',
        },
      )
    } else if (updateTribeResult && updateTribeResult.isSuccess) {
      navigate('/tribes')
    }
  }, [updateTribeResult])

  useEffect(() => {
    const teams = tribe?.teams ? [...tribe.teams] : []
    setSortedTeams(teams.sort((a, b) => a.name.localeCompare(b.name)))
  }, [tribe?.teams])

  const canSave =
    Boolean(tribe) &&
    updatedTribeName.trim().length > 0 &&
    updatedTribeName.trim() !== tribe?.name

  const handleSave = () => {
    if (!tribe) return
    updateTribe({
      id: tribe.id || 0,
      name: updatedTribeName.trim(),
      description: '',
      isActive: true,
      companyId: user.company.id,
      isDeliveryTribe: tribe.isDeliveryTribe,
    })
  }

  const handleSaveTeam = async (values: TeamFormData) => {
    if (!tribe?.id) return false
    const isEdit = teamModal?.mode === 'edit' && teamModal.team
    try {
      if (isEdit) {
        await updateTeam({
          id: teamModal!.team?.id || 0,
          name: values.name,
          description: values.description,
          tribeId: tribe.id,
          clientId: teamModal!.team?.clientId || 0,
          startDate: values.startDate,
          endDate: values.endDate,
        }).unwrap()
        toast.success('Team updated successfully!', {
          toastId: 'team-update-success',
        })
      } else {
        await createTeam({
          name: values.name,
          description: values.description,
          companyId: user.company.id,
          tribeId: tribe.id,
          clientId: 0,
          startDate: values.startDate,
          endDate: values.endDate,
        }).unwrap()
        toast.success('Team created successfully!', {
          toastId: 'team-create-success',
        })
      }
      await refreshTribe()
      setTeamModal(null)
    } catch (error: any) {
      const action = isEdit ? 'updating' : 'creating'
      toast.error(
        `An error occurred when ${action} the team: ${
          error?.data?.error ?? 'Unknown error'
        }`,
        { toastId: 'team-save-error' },
      )
      return false
    }
  }

  return (
    <div className="page-content position-relative">
      {(isLoading || isFetching) && <Loader />}
      <h1 className="tribe-form-title">Edit Tribe</h1>

      <div className="tribe-form">
        <section>
          <div className="tribe-form__section-heading">
            <div className="tribe-form__section-header">
              <h3 className="tribe-form__section-title">General Information</h3>
            </div>
            <p className="tribe-form__required-note">(*) Required</p>
          </div>
          <div className="tribe-form__grid">
            <div className="tribe-form__field">
              <label className="tribe-form__label" htmlFor="tribeName">
                Tribe Name <span className="tribe-form__required">*</span>
              </label>
              <input
                id="tribeName"
                type="text"
                className="tribe-form__input"
                value={updatedTribeName}
                onChange={e => setUpdatedTribeName(e.target.value)}
                maxLength={25}
              />
            </div>
          </div>
        </section>

        <section>
          <div className="tribe-form__section-heading">
            <div className="tribe-form__section-header">
              <h3 className="tribe-form__section-title">Teams</h3>
              <button
                type="button"
                className="tribe-form__add-btn"
                onClick={() => setTeamModal({ mode: 'add' })}
                disabled={!tribe?.id}
              >
                <PlusIcon size={14} weight="regular" color="#ffffff" />
                Add Team
              </button>
            </div>
          </div>

          {sortedTeams.length === 0 ? (
            <div className="tribe-form__empty">
              No teams yet. Click &quot;Add Team&quot; to create one.
            </div>
          ) : (
            <div className="tribe-form__table-wrap">
              <table className="tribe-form__table">
                <thead className="tribe-form__table-head">
                  <tr>
                    <th className="tribe-form__table-th">Team ID</th>
                    <th className="tribe-form__table-th">Created On</th>
                    <th className="tribe-form__table-th">Team Start</th>
                    <th className="tribe-form__table-th">Disband Date</th>
                    <th className="tribe-form__table-th tribe-form__table-th--actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedTeams.map(team => {
                    const hasEndDate = Boolean(team.endDate)
                    return (
                      <tr
                        className="tribe-form__table-row"
                        key={team.id}
                        onClick={() => setTeamModal({ mode: 'edit', team })}
                      >
                        <td className="tribe-form__table-td">{team.name}</td>
                        <td className="tribe-form__table-td">
                          {moment(team.created_at).format('MM/DD/YYYY')}
                        </td>
                        <td className="tribe-form__table-td">
                          {moment(team.startDate).format('MM/DD/YYYY')}
                        </td>
                        <td
                          className={`tribe-form__table-td${
                            hasEndDate ? '' : ' tribe-form__table-td--muted'
                          }`}
                        >
                          {hasEndDate
                            ? moment(team.endDate).format('MM/DD/YYYY')
                            : 'No End Date'}
                        </td>
                        <td className="tribe-form__table-td tribe-form__table-td--actions">
                          <div className="tribe-form__row-actions">
                            <button
                              type="button"
                              className="tribe-form__icon-btn tribe-form__icon-btn--edit"
                              aria-label={`Edit ${team.name}`}
                              onClick={() =>
                                setTeamModal({ mode: 'edit', team })
                              }
                            >
                              <PencilSimpleIcon size={18} weight="regular" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <div className="tribe-form__actions">
        <button
          type="button"
          className="tribe-form__btn tribe-form__btn--cancel"
          onClick={() => navigate('/tribes')}
        >
          Cancel
        </button>
        <button
          type="button"
          className="tribe-form__btn tribe-form__btn--submit"
          disabled={!canSave}
          onClick={handleSave}
        >
          Update
        </button>
      </div>

      <TeamModal
        show={teamModal !== null}
        mode={teamModal?.mode ?? 'add'}
        tribeName={tribe ? tribe.name : ''}
        initialValues={
          teamModal?.team
            ? {
                name: teamModal.team.name,
                description: teamModal.team.description ?? '',
                startDate: teamModal.team.startDate,
                endDate: teamModal.team.endDate,
              }
            : undefined
        }
        onClose={() => setTeamModal(null)}
        onSubmit={handleSaveTeam}
      />
    </div>
  )
}

export default EditTribe
